import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  useTheme,
  Divider,
  Chip
} from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import styled from '@emotion/styled';
import { OrgStaff } from '../../../../modules/accessControl/types';

const StatusTag = styled(Box)<{ status: string }>(({ status }) => ({
  backgroundColor: '#F3F4F6',
  color: '#4B5563',
  padding: '2px 12px',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 600,
  display: 'inline-block',
  marginTop: '8px',
}));

const InfoItem = styled(Box)(({ theme }) => ({
  marginBottom: '16px',
}));

const InfoLabel = styled(Typography)({
  fontSize: '11px',
  fontWeight: 700,
  color: '#5E8E7E',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: '4px',
});

const InfoValue = styled(Typography)({
  fontSize: '14px',
  color: '#2C3E50',
  fontWeight: 500,
  lineHeight: 1.5,
});

const MessageContainer = styled(Box)({
  backgroundColor: '#FFFDE7',
  padding: '14px 16px',
  borderRadius: '8px',
  marginTop: '8px',
  border: '1px solid #FFF59D',
  borderLeft: '4px solid #FBC02D'
});

interface StaffDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  mapping: OrgStaff | null;
}

const StaffDetailDrawer: React.FC<StaffDetailDrawerProps> = ({
  open,
  onClose,
  mapping,
}) => {
  const theme = useTheme();

  const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: 400,
          boxShadow: theme.shadows[15],
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: theme.palette.background.paper,
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#2C3E50', fontSize: '20px' }}>
            Staff Member Details
          </Typography>
          <StatusTag status="Staff">
            Mapping ID: #{mapping?.mapping_id}
          </StatusTag>
        </Box>

        <IconButton onClick={onClose} size="medium" sx={{ color: theme.palette.action.active }}>
          <HighlightOffIcon fontSize="medium" />
        </IconButton>
      </Box>

      <Divider variant="fullWidth" />

      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          bgcolor: theme.palette.background.paper,
          p: 3
        }}
      >
        {mapping && (
          <Box>
            <InfoItem>
              <InfoLabel>Full Name</InfoLabel>
              <InfoValue>{mapping.user.first_name} {mapping.user.last_name}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Email Address</InfoLabel>
              <InfoValue>{mapping.user.email}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Phone Number</InfoLabel>
              <InfoValue>{mapping.user.phone_number || 'N/A'}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>System Role</InfoLabel>
              <InfoValue sx={{ mt: 0.5 }}>
                <Chip 
                  label={mapping.system_role?.role_name || 'N/A'} 
                  size="small" 
                  sx={{ 
                    backgroundColor: '#E8F5E9', 
                    color: '#2E7D32', 
                    fontWeight: 600,
                    fontSize: '12px'
                  }}
                />
              </InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Status</InfoLabel>
              <InfoValue sx={{ mt: 0.5 }}>
                {(() => {
                  if (mapping.user.is_suspended) {
                    return (
                      <Chip 
                        label="Suspended" 
                        color="error" 
                        variant="filled" 
                        size="small" 
                        sx={{ fontWeight: 600, fontSize: '12px' }}
                      />
                    );
                  }
                  if (!mapping.user.is_active) {
                    return (
                      <Chip 
                        label="Deactivated" 
                        color="default" 
                        variant="filled" 
                        size="small" 
                        sx={{ fontWeight: 600, fontSize: '12px' }}
                      />
                    );
                  }
                  return (
                    <Chip 
                      label="Active" 
                      color="success" 
                      variant="filled" 
                      size="small" 
                      sx={{ fontWeight: 600, fontSize: '12px' }}
                    />
                  );
                })()}
              </InfoValue>
            </InfoItem>

            {(mapping.user.is_suspended || !mapping.user.is_active) && (
              <Box sx={{ mt: 3 }}>
                <InfoLabel>Reason / Remark</InfoLabel>
                <MessageContainer>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#5D4037', lineHeight: 1.6, fontWeight: 500, fontSize: '13px' }}>
                    {mapping.user.status_reason || 'No remark provided.'}
                  </Typography>
                </MessageContainer>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default StaffDetailDrawer;
